<template>
  <b-modal
    ref="jobModal"
    id="jobDescriptionModal"
    size="lg"
    modal-class="job-description-modal"
    centered
    hide-footer
    hide-header
    @hidden="onHidden"
  >
    <!-- Job Description Content -->
     <div :class="{'jd-content': loading || errorMessage || !hasValidJobData}">
        <Loader v-if="loading" />
        <div v-else-if="errorMessage" class="error-message description pad-1">
          {{ errorMessage }}
        </div>
        <div v-else-if="!hasValidJobData" class="error-message description pad-1">
          {{ validationErrorMessage }}
        </div>
        <div class="job-content" v-else-if="hasValidJobData">
          <div class="job-header pad-1">
            <div class="company-name-row d-flex justify-content-between align-items-center">
              <span v-if="isInitialScout" class="pickup-badge">ピックアップ求人</span>
              <h3 v-else class="mb-2" style="font-weight: 600; margin-bottom: 0 !important;">{{ currentJob.companyName }}</h3>
              <button
                data-testid="job-modal-close"
                @click="hide"
                class="close-button-header"
                title="閉じる"
              >
                <i class="fas fa-times"></i>
              </button>
            </div>
            <h3 v-if="isInitialScout" class="job-header-company mb-2" style="font-weight: 600; margin-bottom: 0 !important;">{{ currentJob.companyName }}</h3>
            <p class="job-title-clamp" v-html="highlightText(currentJob.job_title, 'job_title')"></p>
          </div>  
          <div v-html="highlightText(currentJob.job_description, 'job_description')" class="description pad-1"></div>
          <p>求人更新日 {{ jobDate }}</p>
        </div>
     </div>

    <div class="modal-footer scout-footer w-100" v-if="(!loading && !errorMessage && hasValidJobData)">
      <div class="scout-button-section">
          <b-button
            @click="handleScout"
            :class="buttonClass"
            :disabled="isScouted"
          >
            {{ isExpired ? 'スカウト希望期間終了' : buttonText }}
          </b-button>
          <p :class="isScouted ? 'scouted-hint' : 'scout-hint'" v-if="showScoutHint">
            {{ scoutHintText }}
          </p>
        </div>
    </div>
    
    <div class="modal-footer navigation-footer w-100" v-if="(!loading && !errorMessage && hasValidJobData)">
      <div class="navigation-buttons" :class="{ 'navigation-buttons--space-between': isInitialScout }">
        <span v-if="isInitialScout"
          >{{ currentIndex + 1 }}/{{ totalJobs }} 件表示</span
        >
        <span v-else style="width: 44px;" class="nav-space"></span>
        <div class="navigation-toggler" style="">
          <span
            v-if="currentIndex > 0"
            style="color: #000000; font-weight: 500;"
          >
            <span
              @click="handlePrev"
              class="prev-link"
              style="cursor: pointer; color: #000000; font-weight: 500;"
            >
              前に戻る  <
            </span>
          </span>
          <span v-if="!(isInitialScout && isLastJob)" style="color: #000000; font-weight: 500;">
            <span
              @click="handleNext"
              class="next-link"
              style="cursor: pointer; color: #000000; font-weight: 500;"
            >
              > 次に進む
            </span>
          </span>
          <button
            v-if="isInitialScout && isLastJob"
            data-testid="job-modal-close-nav"
            type="button"
            class="nav-close-btn"
            @click="hide"
          >
            閉じる
          </button>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import Loader from "@/components/Loader.vue";
import moment from "moment";
export default {
  name: "JobDescriptionModal",
  components: {
    Loader,
  },
  props: {
    errorMessage: {
      type: String,
      required: false,
    },
    currentJob: {
      type: Object,
      required: true,
    },
    currentIndex: {
      type: Number,
      required: true,
    },
    totalJobs: {
      type: Number,
      required: true,
    },
    loading: {
      type: Boolean,
      required: false,
    },
    isJobScouted: {
      type: Boolean,
      required: false,
    },
    hasReachedScoutLimit: {
      type: Boolean, 
      required: false,
    },
    hasCompanyActiveScout: {
      type: Boolean,
      required: false,
      default: false
    },
    company: {
      type: String,
      required: false,
    },
    isInitialScout: {
      type: Boolean,
      required: false,
      default: false,
    }
  },
  computed: {
    isScouted() {
      return this.isJobScouted || this.hasReachedScoutLimit || false;
    },
    /** Show hint when not scouted for this job, or when limit reached (prefer limit message). Hide only when isJobScouted. */
    showScoutHint() {
      return !this.isJobScouted || this.hasReachedScoutLimit;
    },
    isExpired(){
      return this.currentJob.status === 'expired';
    },
    hasValidJobData() {
      return this.currentJob && 
             this.currentJob.companyName && 
             this.currentJob.job_description;
    },
    validationErrorMessage() {
      if (!this.currentJob) return '求人情報が見つかりません。';
      if (!this.currentJob.companyName) return '企業名の情報が不足しています。';
      if (!this.currentJob.job_description) return '求人詳細の情報が不足しています。';
      return null;
    },
    jobDate() {
      const jobToDisplay = this.currentJob.updated_date || this.currentJob.created_date;
      const date = moment(jobToDisplay).format('YYYY/MM/DD');
      return date;
    },
    scoutHintText(){
      if(this.isJobScouted) {
        return '';
      } else if(this.hasReachedScoutLimit) {
        return '企業からスカウトが届きやすくなります';
      }
      return '企業からスカウトが届きやすくなります'
    },
    buttonText() {
      if(this.isJobScouted) {
        return 'スカウト希望中'
      } else {
        return 'スカウト希望する'
      }
    },
    buttonClass() {
      if(this.isExpired) {
        return 'scout-expired-button'
      } else if(this.isJobScouted) {
        return 'scouted-button'
      } else if(this.hasCompanyActiveScout) {
        return 'scout-company-active-button'
      } else if(this.hasReachedScoutLimit) {
        return 'scout-limit-button'
      } else {
        return 'scout-button'
      }
    },
    isLastJob() {
      return this.totalJobs > 0 && this.currentIndex === this.totalJobs - 1;
    },
  },
  methods: {
    show() {
      this.$refs.jobModal.show();
    },
    onHidden() {
      if(this.isInitialScout) this.$emit('close');
    },
    hide() {
      this.$emit('close');
      this.$refs.jobModal.hide();
    },
    handleScout() {
      console.log(this.isScouted)
      if (this.isScouted) {
        return;
      }
      this.$emit("scout");
    },
    handleNext() {
      this.$emit("next");
    },
    handlePrev() {
      this.$emit("prev");
    },
    highlightText(text, field) {
      if (!text || !this.currentJob.highlights || !this.currentJob.highlights.length) return text;
      
      const fieldHighlights = this.currentJob.highlights.find(h => h.path === field);
      if (!fieldHighlights || !fieldHighlights.texts) return text;

      // Get hits and sort by length (longest first)
      const hits = fieldHighlights.texts
        .filter(text => text.type === 'hit')
        .map(text => text.value)
        .sort((a, b) => b.length - a.length);

      if (!hits.length) return text;

      // First handle full-width spaces between hits
      let highlightedText = this.highlightFullWidthSpaces(text, hits);
      // Then highlight the hits
      return this.highlightHits(highlightedText, hits);
    },

    highlightFullWidthSpaces(text, hits) {
      // Create patterns for all possible hit combinations with full-width space
      const patterns = hits.flatMap((hit1, index) =>
        hits.slice(index + 1).map((hit2) => ({
          pattern: new RegExp(
            `(${this.escapeRegExp(hit1)}　${this.escapeRegExp(hit2)})|(${this.escapeRegExp(hit2)}　${this.escapeRegExp(hit1)})`,
            'g'
          ),
          replacement: (_, g1, g2) => {
            const match = g1 || g2;
            const [first, second] = match.split('　');
            return `${first}<span class="highlight">　</span>${second}`;
          },
        }))
      );

      // Apply all patterns at once
      return patterns.reduce(
        (text, { pattern, replacement }) => text.replace(pattern, replacement),
        text
      );
    },

    highlightHits(text, hits) {
      const highlightedPositions = new Set();

      return hits.reduce((text, hit) => {
        const pattern = new RegExp(this.escapeRegExp(hit), 'g');
        return text.replace(pattern, (match, offset) => {
          const positions = Array.from(
            { length: match.length },
            (_, i) => offset + i
          );

          if (positions.every((pos) => !highlightedPositions.has(pos))) {
            positions.forEach((pos) => highlightedPositions.add(pos));
            return `<span class="highlight">${match}</span>`;
          }
          return match;
        });
      }, text);
    },

    escapeRegExp(string) {
      return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }
  },
};
</script>
<style scoped>
.job-header {
  height: 150px;
}

.job-title-clamp {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.scout-button-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 120px;
  padding-top: 20px;
  /* padding-top: 0px; */
}

.scout-footer {
  display: flex;
  justify-content: center;;
  padding: 10px 0px 0px 0px;
}
.scout-hint {
  text-align: center;
  color: #ff0000;
  font-size: 10px;
  margin-top: 0px;
}
.scouted-hint {
  text-emphasis: center;
  color: #ff0000;
  font-size: 10px;
  margin-top: 0px;
}
.placeholder-height {
  height: 10px;
}
.pad-1 {
  padding: 1rem;
}
.description {
  height: 50vh; 
  overflow-y: auto;
  border: 1px solid #E0E7FF;
}

/* Responsive height adjustments */
@media (max-width: 768px) {
  .job-header {
    min-height: 120px;
    height: auto;
  }
  .description {
    height: 40vh;
  }
}

@media (min-width: 1200px) {
  .description {
    height: 45vh;
  }
}

@media (min-width: 1600px) {
  .description {
    height: 55vh;
  }
}
.jd-content {
  height: 60vh; /* Match the height of the job content */
  display: flex;
  flex-direction: column;
}

.error-message {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.error-content {
  text-align: center;
  color: #ff0000;
  font-size: 14px;
  line-height: 1.5;
}

::v-deep .highlight {
  background-color: #ffd700;
  padding: 0 2px;
  border-radius: 2px;
}

::v-deep .job-description-modal .modal-content {
  border: none !important;
  box-shadow: 4px 4px 4px 4px #0000000D !important;
  border-radius: 10px;
}

@media (min-width: 992px) {
  ::v-deep .job-description-modal .modal-dialog.modal-lg {
    max-width: 979px;
  }
}

::v-deep .job-description-modal .modal-body {
  padding: 1rem 1.5rem !important;
}

@media (max-width: 767px) {
  ::v-deep .job-description-modal .modal-body {
    padding: 8px !important;
  }

  .job-header-company {
    font-size: 14px !important;
    word-wrap: break-word;
    overflow-wrap: break-word;
    word-break: break-all;
    line-height: 1.4;
    margin-bottom: 0.5rem !important;
    display: block;
    overflow: visible;
    white-space: normal;
    max-width: calc(100% - 40px);
  }

  .job-title-clamp {
    font-size: 14px !important;
    line-height: 1.4;
  }

  .company-name-row {
    align-items: flex-start !important;
    justify-content: space-between !important;
    display: flex !important;
    width: 100%;
  }

  .company-name-row h3 {
    flex: 1;
    min-width: 0;
  }

  .close-button-header {
    flex-shrink: 0;
    margin-left: 10px;
  }

  .company-name-row h3 {
    font-size: 14px !important;
    word-wrap: break-word;
    overflow-wrap: break-word;
    word-break: break-all;
    line-height: 1.4;
    display: block;
    overflow: visible;
    white-space: normal;
  }
}
::v-deep .job-description-modal .modal-footer {
  background: #ffffff !important;
  /* padding-bottom: 0px; */
}

@media (min-width: 1024px) {
  .scout-footer {
    display: flex;
    justify-content: center;
    gap: 50px;
  }
}

.navigation-footer {
  background: #ffffff !important;
  padding: 5px 20px;
  border-top: none !important;
}

.navigation-buttons {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  width: 100%;
}

.navigation-buttons--space-between {
  justify-content: space-between;
}

.navigation-toggler {
  display: flex;
  align-items: center;
  gap: 30px;
}

.nav-close-btn {
  background: #9CA3AF;
  color: #fff;
  border: none;
  border-radius: 2px;
  padding: 6px 14px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
}

.nav-close-btn:hover {
  opacity: 0.9;
}

.close-button {
  background: none;
  border: none;
  font-size: 18px;
  color: #000000;
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 4px;
  transition: background-color 0.2s;
  min-width: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 0px !important;
}

.close-button:hover {
  background-color: #e6f3f3;
}

.close-button:active {
  background-color: #d1e7e7;
}

.close-button-header {
  background: none;
  border: none;
  font-size: 18px;
  color: #000000;
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 4px;
  transition: background-color 0.2s;
  min-width: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-right: -15px !important;
}

.close-button-header:hover {
  background-color: #e6f3f3;
}

.close-button-header:active {
  background-color: #d1e7e7;
}

.company-name-row {
  width: 100%;
  margin-bottom: 0.5rem;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.company-name-row h3 {
  flex: 1;
  min-width: 0;
}

.company-name-row .close-button-header {
  flex-shrink: 0;
  margin-left: 10px;
}

.company-name-row h3 {
  word-wrap: break-word;
  overflow-wrap: break-word;
  word-break: break-word;
  line-height: 1.4;
  display: block;
  overflow: visible;
  white-space: normal;
}

.pickup-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #009D9E;
  color: white;
  width: 188px;
  height: 26px;
  font-weight: 600;
  font-size: 14px;
}

.next-link {
  transition: color 0.2s;
}

.next-link:hover {
  color: #005a5a;
}

/* Mobile responsive styles for scout button section */
@media (max-width: 768px) {
  .scout-button-section {
    width: 90% !important;
    margin: 0 auto;
  }
  /* .navigation-buttons {
    justify-content: center;
  } */
  .nav-space {
    display: none !important;
  }
}
</style>
